import type { ExplosionRender } from "../../../shared/shop-catalog";
import { drawCustomGrid } from "./CustomArt";

// A one-shot death burst played at the lizard's position during the ~0.6s dying
// phase. A single instance is reused per run: spawn() seeds the random sparks,
// update() advances the clock, render() draws the current frame keyed on the
// equipped explosion's `kind` (mirrors the drawTrailIcon / drawFrame pattern).

const DURATION = 0.6;
const SPARKS = 16;

interface Spark {
  angle: number;
  dist: number;
  size: number;
  rot: number;
  seed: number;
}

export class Explosion {
  private x = 0;
  private y = 0;
  private elapsed = 0;
  private active = false;
  private cfg: ExplosionRender | null = null;
  private sparks: Spark[] = [];

  spawn(x: number, y: number, cfg: ExplosionRender | null): void {
    this.active = !!cfg;
    this.cfg = cfg;
    if (!cfg) return;
    this.x = x;
    this.y = y;
    this.elapsed = 0;
    this.sparks = Array.from({ length: SPARKS }, (_, i) => ({
      angle: (i / SPARKS) * Math.PI * 2 + (Math.random() - 0.5) * 0.4,
      dist: 16 + Math.random() * 26,
      size: 2 + Math.random() * 3,
      rot: Math.random() * Math.PI * 2,
      seed: Math.random(),
    }));
  }

  update(dt: number): void {
    if (!this.active) return;
    this.elapsed += dt;
    if (this.elapsed >= DURATION) this.active = false;
  }

  render(ctx: CanvasRenderingContext2D): void {
    if (!this.active || !this.cfg) return;
    drawExplosion(ctx, this.cfg, this.x, this.y, Math.min(1, this.elapsed / DURATION), this.sparks);
  }
}

function easeOut(p: number): number {
  return 1 - (1 - p) * (1 - p);
}

// Initial bright flash at the center, fading out fast.
function flash(ctx: CanvasRenderingContext2D, x: number, y: number, p: number, color: string): void {
  if (p > 0.35) return;
  ctx.save();
  ctx.globalAlpha = (1 - p / 0.35) * 0.9;
  ctx.fillStyle = color;
  circle(ctx, x, y, 6 + p * 40);
  ctx.restore();
}

type ShapeFn = (
  ctx: CanvasRenderingContext2D,
  px: number,
  py: number,
  size: number,
  s: Spark,
) => void;

// Fling the seeded sparks outward (eased), fading as they go. `gravity` adds a
// downward drift; `drift` lifts them instead (smoke/petals).
function parts(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  p: number,
  sparks: Spark[],
  drift: number,
  shape: ShapeFn,
): void {
  const e = easeOut(p);
  const fade = 1 - p;
  for (const s of sparks) {
    const d = s.dist * e;
    const px = x + Math.cos(s.angle) * d;
    const py = y + Math.sin(s.angle) * d + drift * p * s.dist;
    ctx.globalAlpha = Math.max(0, fade);
    shape(ctx, px, py, s.size * (0.7 + fade * 0.6), s);
  }
  ctx.globalAlpha = 1;
}

function rings(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  p: number,
  color: string,
  count: number,
  maxR: number,
  width: number,
): void {
  ctx.save();
  ctx.strokeStyle = color;
  for (let i = 0; i < count; i += 1) {
    const lp = Math.min(1, p + i * 0.12);
    const r = 4 + easeOut(lp) * maxR;
    ctx.globalAlpha = (1 - lp) * 0.9;
    ctx.lineWidth = width * (1 - lp) + 0.5;
    ctx.beginPath();
    ctx.arc(x, y, r, 0, Math.PI * 2);
    ctx.stroke();
  }
  ctx.restore();
}

function rays(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  p: number,
  color: string,
  n: number,
  len: number,
): void {
  ctx.save();
  ctx.strokeStyle = color;
  ctx.lineCap = "round";
  ctx.globalAlpha = 1 - p;
  const e = easeOut(p);
  for (let i = 0; i < n; i += 1) {
    const a = (i / n) * Math.PI * 2;
    const inner = 4 + e * len * 0.3;
    const outer = 4 + e * len;
    ctx.lineWidth = 2 * (1 - p) + 0.5;
    ctx.beginPath();
    ctx.moveTo(x + Math.cos(a) * inner, y + Math.sin(a) * inner);
    ctx.lineTo(x + Math.cos(a) * outer, y + Math.sin(a) * outer);
    ctx.stroke();
  }
  ctx.restore();
}

export function drawExplosion(
  ctx: CanvasRenderingContext2D,
  cfg: ExplosionRender,
  x: number,
  y: number,
  p: number,
  sparks: Spark[],
): void {
  const c1 = cfg.color;
  const c2 = cfg.color2 ?? cfg.color;

  switch (cfg.kind) {
    case "burst":
      flash(ctx, x, y, p, c1);
      parts(ctx, x, y, p, sparks, 0.2, (g, px, py, s) => {
        g.fillStyle = c1;
        g.fillRect(px - s / 2, py - s / 2, s, s);
      });
      return;
    case "ring":
      rings(ctx, x, y, p, c1, 2, 46, 3);
      return;
    case "shockwave":
      rings(ctx, x, y, p, c1, 1, 60, 6);
      flash(ctx, x, y, p, c1);
      return;
    case "firework":
      flash(ctx, x, y, p, c2);
      parts(ctx, x, y, p, sparks, 0.5, (g, px, py, s, sp) => {
        g.fillStyle = sp.seed > 0.5 ? c1 : c2;
        circle(g, px, py, s * 0.7);
      });
      return;
    case "starburst":
      rays(ctx, x, y, p, c1, 12, 48);
      parts(ctx, x, y, p, sparks, 0, (g, px, py, s) => {
        g.fillStyle = c1;
        star4(g, px, py, s * 1.2);
      });
      return;
    case "confetti":
      parts(ctx, x, y, p, sparks, 0.55, (g, px, py, s, sp) => {
        g.save();
        g.translate(px, py);
        g.rotate(sp.rot + p * 8);
        g.fillStyle = `hsl(${Math.floor(sp.seed * 360)}, 85%, 60%)`;
        g.fillRect(-s / 2, -s / 4, s, s / 2);
        g.restore();
      });
      return;
    case "sparkle":
      parts(ctx, x, y, p, sparks, 0, (g, px, py, s, sp) => {
        g.fillStyle = c1;
        const tw = 0.6 + Math.abs(Math.sin(p * 14 + sp.seed * 6)) * 0.6;
        star4(g, px, py, s * tw);
      });
      return;
    case "nova":
      flash(ctx, x, y, p, c2);
      rings(ctx, x, y, p, c1, 1, 52, 4);
      rays(ctx, x, y, p, c1, 16, 40);
      return;
    case "implode": {
      // sparks rush inward, then a flash.
      const e = easeOut(p);
      ctx.save();
      for (const s of sparks) {
        const d = s.dist * (1 - e);
        ctx.globalAlpha = Math.min(1, p * 1.5);
        ctx.fillStyle = c1;
        circle(ctx, x + Math.cos(s.angle) * d, y + Math.sin(s.angle) * d, s.size * 0.7);
      }
      ctx.restore();
      if (p > 0.6) flash(ctx, x, y, (p - 0.6) / 0.4 * 0.35, c2);
      return;
    }
    case "pixel":
      parts(ctx, x, y, p, sparks, 0.2, (g, px, py, s) => {
        g.fillStyle = c1;
        const r = Math.max(2, Math.round(s));
        g.fillRect(Math.round(px) - r / 2, Math.round(py) - r / 2, r, r);
      });
      return;
    case "smoke":
      parts(ctx, x, y, p, sparks, -0.4, (g, px, py, s) => {
        g.fillStyle = c1;
        g.globalAlpha = g.globalAlpha * 0.6;
        circle(g, px, py, s * (1.2 + p));
      });
      return;
    case "fireball":
      flash(ctx, x, y, p, c2);
      parts(ctx, x, y, p, sparks, -0.3, (g, px, py, s, sp) => {
        g.fillStyle = sp.seed > 0.5 ? c1 : c2;
        circle(g, px, py, s * (1 - p * 0.4));
      });
      return;
    case "electric":
      ctx.save();
      ctx.strokeStyle = c1;
      ctx.lineWidth = 1.5;
      ctx.globalAlpha = 1 - p;
      for (const s of sparks) {
        bolt(ctx, x, y, s.angle, easeOut(p) * (s.dist + 12), s.seed);
      }
      ctx.restore();
      flash(ctx, x, y, p, c1);
      return;
    case "bubble":
      parts(ctx, x, y, p, sparks, -0.5, (g, px, py, s) => {
        g.strokeStyle = c1;
        g.lineWidth = 1;
        g.beginPath();
        g.arc(px, py, s, 0, Math.PI * 2);
        g.stroke();
      });
      return;
    case "petals":
      parts(ctx, x, y, p, sparks, -0.2, (g, px, py, s, sp) => {
        g.save();
        g.translate(px, py);
        g.rotate(sp.rot + p * 3);
        g.fillStyle = sp.seed > 0.6 ? c2 : c1;
        g.beginPath();
        g.ellipse(0, 0, s * 1.3, s * 0.55, 0, 0, Math.PI * 2);
        g.fill();
        g.restore();
      });
      return;
    case "shatter":
      parts(ctx, x, y, p, sparks, 0.6, (g, px, py, s, sp) => {
        g.save();
        g.translate(px, py);
        g.rotate(sp.rot + p * 6);
        g.fillStyle = sp.seed > 0.5 ? c1 : c2;
        g.beginPath();
        g.moveTo(0, -s);
        g.lineTo(s * 0.9, s * 0.7);
        g.lineTo(-s * 0.8, s * 0.6);
        g.closePath();
        g.fill();
        g.restore();
      });
      return;
    case "spiral": {
      const e = easeOut(p);
      ctx.save();
      ctx.globalAlpha = 1 - p;
      sparks.forEach((s, i) => {
        const a = s.angle + p * 5;
        const d = (4 + (i / sparks.length) * 44) * e;
        ctx.fillStyle = i % 2 ? c1 : c2;
        circle(ctx, x + Math.cos(a) * d, y + Math.sin(a) * d, s.size * 0.7);
      });
      ctx.restore();
      return;
    }
    case "comet":
      ctx.save();
      ctx.lineCap = "round";
      ctx.globalAlpha = 1 - p;
      for (let i = 0; i < 6; i += 1) {
        const a = (i / 6) * Math.PI * 2 + 0.3;
        const e = easeOut(p);
        const hx = x + Math.cos(a) * (10 + e * 46);
        const hy = y + Math.sin(a) * (10 + e * 46);
        const tx = x + Math.cos(a) * (4 + e * 20);
        const ty = y + Math.sin(a) * (4 + e * 20);
        ctx.strokeStyle = c1;
        ctx.lineWidth = 3 * (1 - p) + 0.5;
        ctx.beginPath();
        ctx.moveTo(tx, ty);
        ctx.lineTo(hx, hy);
        ctx.stroke();
        ctx.fillStyle = c2;
        circle(ctx, hx, hy, 2.5 * (1 - p) + 0.5);
      }
      ctx.restore();
      return;
    case "prism":
      ctx.save();
      ctx.globalAlpha = 1 - p;
      sparks.forEach((s, i) => {
        const d = s.dist * easeOut(p);
        ctx.fillStyle = `hsl(${(i / sparks.length) * 360}, 85%, 60%)`;
        ctx.fillRect(
          x + Math.cos(s.angle) * d - s.size / 2,
          y + Math.sin(s.angle) * d - s.size / 2,
          s.size,
          s.size,
        );
      });
      ctx.restore();
      return;
    case "void":
      ctx.save();
      ctx.globalAlpha = (1 - p) * 0.9;
      ctx.fillStyle = c1;
      circle(ctx, x, y, 6 + easeOut(p) * 34);
      ctx.restore();
      rings(ctx, x, y, p, c2, 2, 50, 3);
      return;
    case "snow":
      parts(ctx, x, y, p, sparks, 0.3, (g, px, py, s, sp) => {
        g.strokeStyle = c1;
        g.lineWidth = 1;
        snowflake(g, px, py, s, sp.rot + p * 2);
      });
      return;
    case "heart":
      parts(ctx, x, y, p, sparks, -0.3, (g, px, py, s) => {
        g.fillStyle = c1;
        heart(g, px, py, s * 1.3);
      });
      return;
    case "gold":
      flash(ctx, x, y, p, c2);
      rings(ctx, x, y, p, c1, 1, 40, 3);
      parts(ctx, x, y, p, sparks, 0.3, (g, px, py, s) => {
        g.fillStyle = c1;
        circle(g, px, py, s * 0.8);
        g.fillStyle = c2;
        circle(g, px - s * 0.3, py - s * 0.3, s * 0.3);
      });
      return;
    case "plasma":
      parts(ctx, x, y, p, sparks, 0, (g, px, py, s, sp) => {
        const t = (Math.sin(p * 8 + sp.seed * 6) + 1) / 2;
        g.fillStyle = mixHex(c1, c2, t);
        circle(g, px, py, s);
      });
      return;
    case "divine":
      flash(ctx, x, y, p, c2);
      rings(ctx, x, y, p, c2, 1, 50, 4);
      rays(ctx, x, y, p, c2, 18, 46);
      // halo
      ctx.save();
      ctx.globalAlpha = (1 - p) * 0.9;
      ctx.strokeStyle = c2;
      ctx.lineWidth = 2;
      ctx.beginPath();
      ctx.ellipse(x, y - 18 - p * 10, 12, 4, 0, 0, Math.PI * 2);
      ctx.stroke();
      ctx.restore();
      return;
    case "creator": {
      const e = easeOut(p);
      ctx.save();
      ctx.globalAlpha = 1 - p;
      ctx.lineCap = "round";
      for (let i = 0; i < 14; i += 1) {
        const a = (i / 14) * Math.PI * 2 + p;
        ctx.strokeStyle = `hsl(${(i / 14) * 360 + p * 120}, 90%, 60%)`;
        ctx.lineWidth = 2.5 * (1 - p) + 0.5;
        ctx.beginPath();
        ctx.moveTo(x + Math.cos(a) * 5, y + Math.sin(a) * 5);
        ctx.lineTo(x + Math.cos(a) * (5 + e * 50), y + Math.sin(a) * (5 + e * 50));
        ctx.stroke();
      }
      ctx.restore();
      return;
    }
    case "custom":
      if (cfg.cells && cfg.grid) {
        const size = 24 + easeOut(p) * 44;
        ctx.save();
        ctx.globalAlpha = 1 - p;
        ctx.translate(x, y);
        ctx.rotate(p * 1.2);
        drawCustomGrid(ctx, cfg.cells, cfg.grid, -size / 2, -size / 2, size, size);
        ctx.restore();
      }
      return;
  }
}

// --- shape primitives ---

function circle(ctx: CanvasRenderingContext2D, x: number, y: number, r: number): void {
  ctx.beginPath();
  ctx.arc(x, y, Math.max(0, r), 0, Math.PI * 2);
  ctx.fill();
}

function star4(ctx: CanvasRenderingContext2D, x: number, y: number, r: number): void {
  ctx.beginPath();
  ctx.moveTo(x, y - r);
  ctx.lineTo(x + r * 0.3, y - r * 0.3);
  ctx.lineTo(x + r, y);
  ctx.lineTo(x + r * 0.3, y + r * 0.3);
  ctx.lineTo(x, y + r);
  ctx.lineTo(x - r * 0.3, y + r * 0.3);
  ctx.lineTo(x - r, y);
  ctx.lineTo(x - r * 0.3, y - r * 0.3);
  ctx.closePath();
  ctx.fill();
}

function heart(ctx: CanvasRenderingContext2D, x: number, y: number, r: number): void {
  ctx.beginPath();
  ctx.moveTo(x, y + r * 0.4);
  ctx.bezierCurveTo(x + r, y - r * 0.4, x + r * 0.6, y - r, x, y - r * 0.3);
  ctx.bezierCurveTo(x - r * 0.6, y - r, x - r, y - r * 0.4, x, y + r * 0.4);
  ctx.closePath();
  ctx.fill();
}

function snowflake(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  r: number,
  rot: number,
): void {
  for (let i = 0; i < 3; i += 1) {
    const a = rot + i * (Math.PI / 3);
    ctx.beginPath();
    ctx.moveTo(x - Math.cos(a) * r, y - Math.sin(a) * r);
    ctx.lineTo(x + Math.cos(a) * r, y + Math.sin(a) * r);
    ctx.stroke();
  }
}

function bolt(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  angle: number,
  len: number,
  seed: number,
): void {
  const steps = 4;
  const perp = angle + Math.PI / 2;
  ctx.beginPath();
  ctx.moveTo(x, y);
  for (let i = 1; i <= steps; i += 1) {
    const t = (i / steps) * len;
    const jitter = (((seed * 100 + i * 37) % 7) - 3) * 1.5;
    ctx.lineTo(
      x + Math.cos(angle) * t + Math.cos(perp) * jitter,
      y + Math.sin(angle) * t + Math.sin(perp) * jitter,
    );
  }
  ctx.stroke();
}

function mixHex(a: string, b: string, t: number): string {
  const ca = parseHex(a);
  const cb = parseHex(b);
  if (!ca || !cb) return a;
  return `rgb(${Math.round(ca[0] + (cb[0] - ca[0]) * t)},${Math.round(
    ca[1] + (cb[1] - ca[1]) * t,
  )},${Math.round(ca[2] + (cb[2] - ca[2]) * t)})`;
}

function parseHex(hex: string): [number, number, number] | null {
  if (!hex.startsWith("#")) return null;
  if (hex.length === 4) {
    return [
      parseInt(hex[1]! + hex[1]!, 16),
      parseInt(hex[2]! + hex[2]!, 16),
      parseInt(hex[3]! + hex[3]!, 16),
    ];
  }
  if (hex.length === 7) {
    return [
      parseInt(hex.slice(1, 3), 16),
      parseInt(hex.slice(3, 5), 16),
      parseInt(hex.slice(5, 7), 16),
    ];
  }
  return null;
}
